import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { ParallelActivityGroup } from "./ParallelActivityGroup";
import { ActivityStep } from "./PatientCard";
import { formatActivityTimestamp } from "@/lib/dataTransformers";
import { StaffInfoDisplay } from "./StaffInfoDisplay";
import { getTATColorClass } from "@/lib/tatHelpers";
import { useMemo } from "react";
import statusSkippedIcon from "@/assets/status-skipped.svg";

interface ActivityTimelineProps {
  steps: ActivityStep[];
}

// Helper function to format duration
const formatDuration = (minutes: number | undefined): string => {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
};

export const ActivityTimeline = ({ steps }: ActivityTimelineProps) => {
  // Calculate elapsed time for in-progress activities (from assigned_at for TAT tracking)
  const calculateElapsedTime = (assignedAt?: string, startedAt?: string): string => {
    // Use assigned_at for TAT tracking, fallback to started_at for backwards compatibility
    const start = assignedAt || startedAt;
    if (!start) return '00:00:00';
    
    const startDate = new Date(start);
    const now = new Date();
    const diffMs = now.getTime() - startDate.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    const seconds = diffSecs % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {steps.map((step, index) => {
        // Handle parallel activity groups (side-by-side activities)
        if (step.isParallelGroup && step.parallelActivities) {
          return (
            <div key={step.id} className="relative">
              <ParallelActivityGroup
                parallelActivities={step.parallelActivities}
                completedCount={step.completedCount || 0}
                totalCount={step.totalCount || step.parallelActivities.length}
              />
            </div>
          );
        }

        // Handle regular steps
        return (
          <div key={step.id} className="flex gap-4 relative">
            {/* Connecting Line - Green until in-progress, gray for skipped, then default */}
            {index < steps.length - 1 && (
              <div className={cn(
                "absolute left-[9px] top-6 w-0.5 h-[calc(100%+24px)]",
                step.status === "completed" ? "bg-success" 
                  : step.status === "skipped" ? "bg-muted-foreground"
                  : "bg-border"
              )} />
            )}

            {/* Status Indicator */}
            <div className="relative flex-shrink-0 mt-0.5">
              {step.status === "completed" ? (
                <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                  <Check className="w-3 h-3 text-success-foreground" strokeWidth={3} />
                </div>
              ) : step.status === "skipped" ? (
                <div className="w-5 h-5 rounded-full bg-muted-foreground flex items-center justify-center">
                  <img src={statusSkippedIcon} alt="Skipped" className="w-3 h-3 invert" />
                </div>
              ) : step.status === "in-progress" ? (
                <div className="w-5 h-5 rounded-full border-2 border-success bg-background flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-success" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-border bg-background" />
              )}
            </div>

            {/* Step Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                {/* Left side - Step info */}
                <div className="flex-1 min-w-0">
                  <h4 className={cn(
                    "font-semibold text-sm",
                    step.status === "skipped" ? "text-muted-foreground line-through" : "text-foreground"
                  )}>{step.name}</h4>

                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {step.description}
                    </p>
                  )}

                  {/* Display staff info based on status */}
                  {step.status === "completed" && step.completedBy && (
                    <div className="mt-2">
                      <StaffInfoDisplay 
                        staff={step.completedBy}
                        label="Completed by"
                        size="sm"
                      />
                    </div>
                  )}

                  {step.status === "in-progress" && step.assignedTo && (
                    <div className="mt-2">
                      <StaffInfoDisplay 
                        staff={step.assignedTo}
                        label="Assigned to"
                        size="sm"
                      />
                    </div>
                  )}

                  {/* Legacy staff display for backward compatibility */}
                  {!step.completedBy && !step.assignedTo && (step.staffName || step.doctor) && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                      <span>{step.staffName || step.doctor}</span>
                    </div>
                  )}

                  {/* Show notes if available */}
                  {step.notes && (
                    <div className="mt-2 text-xs text-muted-foreground bg-muted/30 p-1 rounded">
                      <span className="font-medium">Notes:</span> {step.notes}
                    </div>
                  )}
                </div>

                {/* Right side - Status-specific info */}
                <div className="flex flex-col items-end flex-shrink-0 gap-1">
                  {step.status === 'completed' && (
                    <>
                      <div className="text-xs text-muted-foreground">Completed:</div>
                      <span className="text-xs whitespace-nowrap">
                        {formatActivityTimestamp(step.completionTime || step.timestamp)}
                      </span>
                      <div className="text-xs text-muted-foreground mt-2">Duration:</div>
                      <span className="bg-success/10 text-success px-1.5 py-0.5 rounded text-xs font-medium">
                        {formatDuration(step.timeTakenMinutes)}
                      </span>
                    </>
                  )}

                  {step.status === 'in-progress' && (
                    <>
                      <div className="text-xs text-muted-foreground">Assigned:</div>
                      <span className="text-xs whitespace-nowrap">
                        {formatActivityTimestamp(step.assignedAt || step.startedAt)}
                      </span>
                      <div className="text-xs text-muted-foreground mt-2">Elapsed:</div>
                      <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-xs font-medium font-mono">
                        {calculateElapsedTime(step.assignedAt, step.startedAt)}
                      </span>
                    </>
                  )}

                  {step.status === 'skipped' && (
                    <>
                      <div className="text-xs text-muted-foreground">Task Skipped:</div>
                      <span className="text-xs whitespace-nowrap">
                        {formatActivityTimestamp(step.completionTime || step.timestamp)}
                      </span>
                    </>
                  )}

                  {/* Pending activities show no timestamp/details */}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
